import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Sparkles, Eye, EyeOff, UserPlus, Mail, ArrowRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import client from '../api/client'

export default function Register() {
  const [searchParams] = useSearchParams()
  const [name, setName] = useState('')
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [registered, setRegistered] = useState(false)
  const [resending, setResending] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const { register } = useAuthStore()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name || !email || !password) {
      toast.error('Заполните все поля')
      return
    }

    if (password.length < 6) {
      toast.error('Пароль должен быть минимум 6 символов')
      return
    }

    if (password !== confirmPassword) {
      toast.error('Пароли не совпадают')
      return
    }

    if (!agreed) {
      toast.error('Необходимо согласиться с обработкой персональных данных')
      return
    }

    setIsLoading(true)
    try {
      await register(email, password, name)
      setRegistered(true)
      toast.success('Аккаунт создан! Проверьте почту для подтверждения')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка регистрации')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    try {
      await client.post('/auth/resend-verification', { email })
      toast.success('Письмо отправлено повторно')
    } catch {
      toast.error('Ошибка при отправке')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f0f1a]">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-indigo-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">Chat-Neuro.ru</span>
          </Link>
        </div>

        {/* Content */}
        <div className="glass-card p-8">
          {!registered ? (
            <>
              <h1 className="text-2xl font-bold text-white mb-1">Регистрация</h1>
              <p className="text-sm text-gray-500 mb-6">
                Создайте аккаунт и получите доступ к{' '}
                <span className="text-amber-400 font-medium">бесплатным моделям</span> без ограничений
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Имя</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ваше имя"
                    className="input-field"
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="input-field"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Пароль</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Минимум 6 символов"
                      className="input-field pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Подтвердите пароль</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Повторите пароль"
                    className="input-field"
                    autoComplete="new-password"
                  />
                </div>

                {/* Согласие на обработку ПДн */}
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-white/5 text-indigo-500 focus:ring-indigo-500/50 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="text-[11px] text-gray-500 leading-relaxed group-hover:text-gray-400 transition-colors">
                    Я согласен на обработку персональных данных согласно{' '}
                    <Link to="/privacy" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                      Политике конфиденциальности
                    </Link>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="gradient-btn w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <UserPlus className="w-4 h-4" />
                  {isLoading ? 'Создание...' : 'Создать аккаунт'}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-500/10 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-indigo-400" />
                </div>
                <h1 className="text-xl font-bold text-white mb-2">Проверьте почту</h1>
                <p className="text-sm text-gray-400 mb-2">
                  Мы отправили письмо на <strong className="text-gray-200">{email}</strong>
                </p>
                <p className="text-xs text-gray-500 mb-6">
                  Перейдите по ссылке в письме, чтобы подтвердить email и получить бонусные токены.
                </p>
                <p className="text-xs text-amber-400/80 mb-6">
                  💡 Не видите письма? Проверьте папку <strong>«Спам»</strong> или <strong>«Промоакции»</strong>
                  — наши письма иногда попадают туда.
                </p>

                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="w-full gradient-btn py-3 rounded-xl text-sm mb-3 flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <Mail className="w-4 h-4" />
                  {resending ? 'Отправка...' : 'Отправить ещё раз'}
                </button>

                <button
                  onClick={() => navigate('/chat')}
                  className="w-full py-3 rounded-xl text-sm text-gray-400 hover:text-white transition-colors border border-white/10 flex items-center justify-center gap-2"
                >
                  Продолжить без подтверждения
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Уже есть аккаунт?{' '}
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium">
                Войти
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
